import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchableSelect from '../SearchableSelect';
import { TenantAdmin } from '../../types';

export interface MasterUserTenantDraft {
  key: string;
  tenantId: number | null;
  role: string;
  isDefault: boolean;
}

const ROLE_OPTIONS = ['Admin', 'User', 'Vendedor', 'Supervisor', 'Operador', 'SUPERADMIN'].map(
  (role) => ({ value: role, label: role }),
);

interface MasterUserTenantsSectionProps {
  tenants: TenantAdmin[];
  rows: MasterUserTenantDraft[];
  disabled?: boolean;
  onChange: (rows: MasterUserTenantDraft[]) => void;
}

const createRow = (): MasterUserTenantDraft => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  tenantId: null,
  role: 'User',
  isDefault: false,
});

const MasterUserTenantsSection: React.FC<MasterUserTenantsSectionProps> = ({
  tenants,
  rows,
  disabled = false,
  onChange,
}) => {
  const tenantOptions = useMemo(
    () =>
      tenants.map((tenant) => ({
        value: tenant.id,
        label: `${tenant.code} — ${tenant.name}`,
      })),
    [tenants],
  );

  const usedTenantIds = useMemo(
    () => new Set(rows.map((row) => row.tenantId).filter((id): id is number => id != null)),
    [rows],
  );

  const updateRow = (key: string, patch: Partial<MasterUserTenantDraft>) => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const removeRow = (key: string) => {
    onChange(rows.filter((row) => row.key !== key));
  };

  const addRow = () => {
    onChange([...rows, createRow()]);
  };

  const setDefault = (key: string, checked: boolean) => {
    if (!checked) {
      updateRow(key, { isDefault: false });
      return;
    }
    onChange(
      rows.map((row) => ({
        ...row,
        isDefault: row.key === key,
      })),
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
            Asignación a tenants
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vincula el usuario a uno o más tenants con su rol en cada empresa.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
          sx={{ textTransform: 'none' }}
        >
          Agregar tenant
        </Button>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          El usuario no está asignado a ningún tenant.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Tenant</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }} align="center">
                  Por defecto
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 72 }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const optionsForRow = tenantOptions.filter(
                  (option) => option.value === row.tenantId || !usedTenantIds.has(option.value),
                );

                return (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <SearchableSelect
                        label="Tenant"
                        value={row.tenantId}
                        onChange={(value) => updateRow(row.key, { tenantId: value })}
                        options={optionsForRow}
                        disabled={disabled}
                        sx={{ minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <SearchableSelect
                        label="Rol"
                        value={row.role}
                        onChange={(value) => updateRow(row.key, { role: value ?? 'User' })}
                        options={ROLE_OPTIONS}
                        disabled={disabled}
                        sx={{ minWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={row.isDefault}
                            onChange={(e) => setDefault(row.key, e.target.checked)}
                            disabled={disabled || row.tenantId == null}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow(row.key)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MasterUserTenantsSection;

export function mapTenantsToDrafts(
  assignments: Array<{
    tenantId: number;
    role: string;
    isDefault: boolean;
  }>,
): MasterUserTenantDraft[] {
  return assignments.map((assignment) => ({
    key: `tenant-${assignment.tenantId}`,
    tenantId: assignment.tenantId,
    role: assignment.role || 'User',
    isDefault: assignment.isDefault,
  }));
}

export function draftsToTenantInputs(
  rows: MasterUserTenantDraft[],
): Array<{ tenantId: number; role: string; isDefault: boolean }> {
  return rows
    .filter((row): row is MasterUserTenantDraft & { tenantId: number } => row.tenantId != null)
    .map((row) => ({
      tenantId: row.tenantId,
      role: row.role.trim() || 'User',
      isDefault: row.isDefault,
    }));
}

export function validateTenantDrafts(rows: MasterUserTenantDraft[]): string | null {
  const assigned = draftsToTenantInputs(rows);
  const tenantIds = rows.map((row) => row.tenantId);
  if (tenantIds.some((id) => id == null)) {
    return 'Seleccione un tenant en cada fila o elimine las filas incompletas.';
  }
  const unique = new Set(assigned.map((row) => row.tenantId));
  if (unique.size !== assigned.length) {
    return 'No puede repetir el mismo tenant en más de una fila.';
  }
  return null;
}
